const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

function normalizeNetworkKeyword(network = {}) {
  return [
    network?.name,
    network?.symbol,
    network?.rpcUrl,
    network?.scanUrl,
    network?.explorerUrl
  ].join(' ').toLowerCase();
}

export function resolveNetworkType(network = {}) {
  const keyword = normalizeNetworkKeyword(network);
  if (keyword.includes('trongrid.io') || keyword.includes('tron') || keyword.includes('trx')) {
    return 'tron';
  }
  if (keyword.includes('go.getblock.io') || keyword.includes('bitcoin') || keyword.includes('btc')) {
    return 'bitcoin';
  }
  return 'evm';
}

function normalizePrivateKey(privateKey = '') {
  const value = String(privateKey || '').trim();
  if (!value) {
    throw new Error('Private key is empty');
  }
  return value.startsWith('0x') || value.startsWith('0X') ? value : `0x${value}`;
}

function bytesToHex(bytes = []) {
  return `0x${Array.from(bytes).map((item) => item.toString(16).padStart(2, '0')).join('')}`;
}

function concatBytes(...groups) {
  const total = groups.reduce((sum, item) => sum + item.length, 0);
  const output = new Uint8Array(total);
  let offset = 0;
  groups.forEach((item) => {
    output.set(item, offset);
    offset += item.length;
  });
  return output;
}

function base58Encode(bytes) {
  if (!bytes.length) {
    return '';
  }

  let value = 0n;
  bytes.forEach((byte) => {
    value = (value << 8n) + BigInt(byte);
  });

  let output = '';
  while (value > 0n) {
    const mod = Number(value % 58n);
    output = `${BASE58_ALPHABET[mod]}${output}`;
    value /= 58n;
  }

  for (let i = 0; i < bytes.length && bytes[i] === 0; i += 1) {
    output = `1${output}`;
  }

  return output || '1';
}

function checksumBytes(ethers, payload) {
  const first = ethers.getBytes(ethers.sha256(payload));
  const second = ethers.getBytes(ethers.sha256(first));
  return second.slice(0, 4);
}

function base58CheckEncode(ethers, payload) {
  return base58Encode(concatBytes(payload, checksumBytes(ethers, payload)));
}

async function deriveFromMnemonic(ethers, phrase, path) {
  const wallet = ethers.HDNodeWallet.fromPhrase(String(phrase || '').trim(), '', path);
  return normalizePrivateKey(wallet.privateKey);
}

function buildTronAddress(ethers, privateKey) {
  const publicKey = ethers.getBytes(ethers.SigningKey.computePublicKey(privateKey, false)).slice(1);
  const hash = ethers.getBytes(ethers.keccak256(publicKey));
  const payload = concatBytes(Uint8Array.from([0x41]), hash.slice(-20));
  return base58CheckEncode(ethers, payload);
}

function buildBitcoinAddress(ethers, privateKey) {
  const publicKey = ethers.getBytes(ethers.SigningKey.computePublicKey(privateKey, true));
  const sha = ethers.getBytes(ethers.sha256(publicKey));
  const ripe = ethers.getBytes(ethers.ripemd160(sha));
  const payload = concatBytes(Uint8Array.from([0x00]), ripe);
  return base58CheckEncode(ethers, payload);
}

export async function derivePrivateKeyByNetwork(secret, type = 'mnemonic', network = {}) {
  const value = String(secret || '').trim();
  if (!value) {
    return '';
  }

  if (String(type || 'mnemonic') === 'privateKey') {
    return normalizePrivateKey(value);
  }

  const ethers = await import('ethers');
  const networkType = resolveNetworkType(network);

  if (networkType === 'tron') {
    return deriveFromMnemonic(ethers, value, "m/44'/195'/0'/0/0");
  }

  if (networkType === 'bitcoin') {
    return deriveFromMnemonic(ethers, value, "m/44'/0'/0'/0/0");
  }

  return deriveFromMnemonic(ethers, value, "m/44'/60'/0'/0/0");
}

export async function deriveAccountAddresses(secret, type = 'mnemonic') {
  const ethers = await import('ethers');
  const value = String(secret || '').trim();
  if (!value) {
    return {
      address: '',
      ethAddress: '',
      tronAddress: '',
      bitcoinAddress: ''
    };
  }

  let evmPrivateKey = '';
  let tronPrivateKey = '';
  let bitcoinPrivateKey = '';

  if (String(type || 'mnemonic') === 'privateKey') {
    evmPrivateKey = normalizePrivateKey(value);
    tronPrivateKey = evmPrivateKey;
    bitcoinPrivateKey = evmPrivateKey;
  } else {
    evmPrivateKey = await deriveFromMnemonic(ethers, value, "m/44'/60'/0'/0/0");
    tronPrivateKey = await deriveFromMnemonic(ethers, value, "m/44'/195'/0'/0/0");
    bitcoinPrivateKey = await deriveFromMnemonic(ethers, value, "m/44'/0'/0'/0/0");
  }

  const evmWallet = new ethers.Wallet(evmPrivateKey);
  return {
    address: evmWallet.address,
    ethAddress: evmWallet.address,
    tronAddress: buildTronAddress(ethers, tronPrivateKey),
    bitcoinAddress: buildBitcoinAddress(ethers, bitcoinPrivateKey)
  };
}
